/**
 * Download Utilities
 * 
 * Helper functions for downloading images from various sources
 * (base64, blob, presigned URLs, S3 cross-origin URLs)
 */

/**
 * Check if a URL is a cross-origin S3 URL (presigned or direct)
 */
const isS3Url = (url: string): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('.s3.') ||
    lower.includes('s3.amazonaws.com') ||
    lower.includes('s3.us-west-2.amazonaws.com') ||
    lower.includes('amazonaws.com/')
  );
};

/**
 * Check if a URL is a cross-origin S3 presigned URL
 */
const isS3PresignedUrl = (url: string): boolean => {
  return isS3Url(url) && (
    url.includes('X-Amz-') ||
    url.includes('AWSAccessKeyId=') ||
    url.includes('Signature=')
  );
};

/**
 * Download an image from a URL or base64 data
 * Handles cross-origin S3 presigned URLs by proxying through backend
 * @param imageSource - URL, base64 data URL, or blob URL
 * @param filename - Desired filename for download
 * @param isBase64 - Whether the source is base64 encoded
 */
export const downloadImage = async (
  imageSource: string,
  filename?: string,
  isBase64: boolean = false
): Promise<void> => {
  try {
    let blob: Blob;
    let finalFilename = filename || generateDefaultFilename();

    if (isBase64 || imageSource.startsWith('data:')) {
      // Handle base64 data URL
      blob = await base64ToBlob(imageSource);
    } else if (isS3Url(imageSource)) {
      // S3 URLs — always proxy so backend can enforce Exif JPEG for AI downloads
      console.log('📥 [Download] S3 URL detected, proxying through backend...');
      blob = await fetchViaBackendProxy(imageSource);
    } else {
      // Handle same-origin URLs (backend API, blob URLs, etc.)
      const response = await fetch(imageSource);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      blob = await response.blob();
    }

    // Create download link and trigger download
    triggerBlobDownload(blob, finalFilename);
  } catch (error) {
    console.error('Error downloading image:', error);
    throw error;
  }
};

/**
 * Fetch an S3 image through the backend proxy to avoid CORS / expired presigns.
 * Prefer s3_key (server GetObject) when available.
 */
const fetchViaBackendProxy = async (
  s3Url?: string,
  s3Key?: string
): Promise<Blob> => {
  const token = localStorage.getItem('auth_token');
  const body: { url?: string; s3_key?: string } = {};
  if (s3Key) body.s3_key = s3Key;
  else if (s3Url) body.url = s3Url;
  else throw new Error('proxy download requires s3_key or url');

  const response = await fetch('/api/v1/s3/proxy-download', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const errJson = await response.json();
      if (errJson?.detail) detail = String(errJson.detail);
    } catch {
      /* ignore */
    }
    throw new Error(`Backend proxy download failed: ${detail}`);
  }

  const blob = await response.blob();
  // Guard: never save S3 error XML as .jpg
  if (blob.type.includes('xml') || blob.type.includes('text')) {
    throw new Error('Download returned an error payload, not an image');
  }
  const probe = await blob.slice(0, 5).arrayBuffer();
  const head = new Uint8Array(probe);
  // JPEG SOI is FF D8; reject '<?xml'
  if (head[0] === 0x3c /* < */) {
    throw new Error('Download URL expired or denied. Refresh the page and try again.');
  }
  return blob;
};

/**
 * Trigger a browser download from a Blob
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Convert base64 data URL to Blob
 * @param base64Data - Base64 encoded data URL or raw base64 string
 */
const base64ToBlob = async (base64Data: string): Promise<Blob> => {
  // Extract base64 string and mime type
  let base64String = base64Data;
  let mimeType = 'image/jpeg'; // Default

  if (base64Data.startsWith('data:')) {
    const matches = base64Data.match(/^data:([^;]+);base64,(.+)$/);
    if (matches && matches[1] && matches[2]) {
      mimeType = matches[1];
      base64String = matches[2];
    } else {
      throw new Error('Invalid base64 data URL format');
    }
  }

  // Decode base64 to binary
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
};

/**
 * Generate a default filename with timestamp
 * @param prefix - Optional prefix for filename
 * @param extension - File extension (default: jpg)
 */
export const generateDefaultFilename = (
  prefix: string = 'gynhub_ai_image',
  extension: string = 'jpg'
): string => {
  const timestamp = new Date().toISOString()
    .replace(/[-:]/g, '')
    .replace('T', '_')
    .split('.')[0];
  return `${prefix}_${timestamp}.${extension}`;
};

/**
 * Download image with a specific filename pattern
 * @param imageSource - URL or base64 data (optional when s3Key provided)
 * @param patientId - Patient ID for filename
 * @param studyDate - Study date for filename (YYYYMMDD)
 * @param seriesDescription - Description for filename
 * @param isBase64 - Whether source is base64
 * @param s3Key - Prefer stable S3 key (avoids expired presigned URLs)
 */
export const downloadAIImage = async (
  imageSource: string,
  patientId?: string,
  studyDate?: string,
  seriesDescription?: string,
  isBase64: boolean = false,
  s3Key?: string
): Promise<void> => {
  const parts = ['gynhub_ai'];
  
  if (patientId) parts.push(`patient_${patientId}`);
  if (studyDate) parts.push(studyDate);
  if (seriesDescription) {
    // Sanitize series description for filename
    const sanitized = seriesDescription
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    parts.push(sanitized);
  }
  
  // Always .jpg — backend proxy converts stills to Exif JPEG
  const filename = generateDefaultFilename(parts.join('_'), 'jpg');

  let blob: Blob;
  if (s3Key) {
    blob = await fetchViaBackendProxy(undefined, s3Key);
  } else if (isBase64 || imageSource.startsWith('data:')) {
    blob = await base64ToBlob(imageSource);
    // Client-side base64 path: only accept JPEG to avoid .jpg-over-PNG
    if (blob.type && !/image\/jpe?g/i.test(blob.type)) {
      throw new Error(
        `AI download expects JPEG bytes, got ${blob.type || 'unknown'}. Re-save via gallery download.`
      );
    }
  } else if (isS3Url(imageSource) || isS3PresignedUrl(imageSource)) {
    blob = await fetchViaBackendProxy(imageSource);
  } else {
    // Prefer same-origin fetch; if it yields non-JPEG, fail rather than lie about extension
    const response = await fetch(imageSource, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    blob = await response.blob();
    if (blob.type && !/image\/jpe?g/i.test(blob.type) && blob.type !== 'application/octet-stream') {
      throw new Error(
        `AI download expects JPEG bytes, got ${blob.type}. Use an S3-backed AI image URL.`
      );
    }
  }

  triggerBlobDownload(blob, filename);
};

/**
 * Download a video Blob as MP4 — converts WebM via backend ffmpeg when needed.
 * Filename excludes patient ID to avoid PHI leakage.
 */
export const ensureMp4VideoBlob = async (videoBlob: Blob): Promise<Blob> => {
  if (videoBlob.type.includes('mp4')) {
    return videoBlob;
  }

  const token = localStorage.getItem('auth_token');
  const form = new FormData();
  const inputName = videoBlob.type.includes('webm') ? 'input.webm' : 'input.bin';
  form.append('file', videoBlob, inputName);

  const response = await fetch('/api/v1/ai/webm-to-mp4', {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  });

  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const errJson = await response.json();
      if (errJson?.detail) detail = String(errJson.detail);
    } catch {
      /* ignore */
    }
    throw new Error(`No se pudo convertir el video a MP4: ${detail}`);
  }

  const mp4 = await response.blob();
  if (!mp4.size) {
    throw new Error('Conversión a MP4 devolvió un archivo vacío');
  }
  return new Blob([mp4], { type: 'video/mp4' });
};

export const downloadTransformationVideo = async (
  videoBlob: Blob,
  studyDate?: string
): Promise<void> => {
  const mp4Blob = await ensureMp4VideoBlob(videoBlob);
  const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0];
  const filename = `GynHub_AI_Transformation_${studyDate || timestamp}.mp4`;
  triggerBlobDownload(mp4Blob, filename);
};

/**
 * Download a GIF Blob as file
 */
export const downloadTransformationGif = (
  gifBlob: Blob,
  studyDate?: string
): void => {
  const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0];
  const filename = `GynHub_AI_Transformation_${studyDate || timestamp}.gif`;
  triggerBlobDownload(gifBlob, filename);
};

/**
 * Share video via Web Share API (mobile-friendly)
 */
export const shareTransformationVideo = async (
  videoBlob: Blob
): Promise<boolean> => {
  if (!navigator.canShare) return false;

  const mp4Blob = await ensureMp4VideoBlob(videoBlob);
  const file = new File([mp4Blob], `AI_Transformation.mp4`, { type: 'video/mp4' });

  if (navigator.canShare({ files: [file] })) {
    await navigator.share({
      title: 'AI Image Transformation',
      files: [file],
    });
    return true;
  }
  return false;
};

/**
 * Check if the browser supports downloads
 */
export const isDownloadSupported = (): boolean => {
  return typeof document !== 'undefined' && 'download' in document.createElement('a');
};
